import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import SimpleDocumentUpload from './SimpleDocumentUpload';
import KYCAnalytics from './KYCAnalytics';
import api from '../api/api';
import KYCStatusManager from '../utils/kycStatusManager';
import KYCDebugHelper from '../utils/kycDebugHelper';
import './KYCVerificationCenter.css';

const KYCVerificationCenter = () => {
  const { currentUser } = useAuth();
  const [verificationState, setVerificationState] = useState({
    currentStep: 'document_upload',
    progress: 0,
    isVerifying: false,
    results: null,
    error: null
  });

  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('verification');

  const [kycData, setKycData] = useState({
    merchantId: currentUser?.id || currentUser?.merchantId || currentUser?.uid || 'demo-merchant',
    personalInfo: {},
    businessInfo: {},
    documents: [],
    verificationResults: {}
  });

  const [verificationSteps, setVerificationSteps] = useState([
    { id: 'document_upload', label: 'Document Upload', status: 'active', icon: '📄' },
    { id: 'authenticity_check', label: 'Authenticity Check', status: 'pending', icon: '🔍' },
    { id: 'data_extraction', label: 'Data Extraction', status: 'pending', icon: '📊' },
    { id: 'face_verification', label: 'Face Verification', status: 'pending', icon: '👤' },
    { id: 'consistency_check', label: 'Consistency Check', status: 'pending', icon: '✅' },
    { id: 'final_review', label: 'Final Review', status: 'pending', icon: '🎯' }
  ]);

  const fetchKYCData = useCallback(async () => {
    try {
      // Fetch KYC status from API
      const response = await api.get(`/api/kyc/status/${kycData.merchantId}`);
      
      if (response.success) {
        setKycData(prev => ({
          ...prev,
          personalInfo: response.data.personalInfo,
          businessInfo: response.data.businessInfo,
          verificationResults: response.data.verificationResults
        }));
      }
    } catch (error) {
      console.error('Error fetching KYC data:', error);
    }
  }, [kycData.merchantId]);

  useEffect(() => {
    fetchKYCData();
  }, [fetchKYCData]);



  const handleDocumentUploaded = (documentType, file) => {
    setKycData(prev => ({
      ...prev,
      documents: [...prev.documents, { type: documentType, file, uploadedAt: new Date() }]
    }));
  };

  const handleVerificationComplete = (results) => {
    console.log('Verification complete callback triggered:', results);
    // Update the documents in kycData from the SimpleDocumentUpload component
    setKycData(prev => ({
      ...prev,
      documents: results.documents.map(doc => ({
        type: doc.documentType,
        file: { name: doc.fileName }, // Mock file object
        uploadedAt: new Date(doc.uploadedAt)
      }))
    }));
    setSuccess('Documents uploaded successfully! You can now start comprehensive verification.');
  };

  const startComprehensiveVerification = async () => {
    console.log('Starting comprehensive verification...');
    
    // Get documents from localStorage
    const storageKey = `kyc_documents_${kycData.merchantId}`;
    const savedDocuments = localStorage.getItem(storageKey);
    let documentsToVerify = [];
    
    if (savedDocuments) {
      const parsedDocs = JSON.parse(savedDocuments);
      documentsToVerify = parsedDocs.map(doc => ({
        type: doc.documentType,
        file: { name: doc.fileName },
        uploadedAt: new Date(doc.uploadedAt)
      }));
    }

    console.log('Documents to verify:', documentsToVerify);

    // If no documents, create mock documents for demo
    if (documentsToVerify.length === 0) {
      console.log('No documents found, creating mock documents for demo');
      documentsToVerify = [
        {
          type: 'PAN_CARD',
          file: { name: 'pan_card_demo.jpg' },
          uploadedAt: new Date()
        },
        {
          type: 'AADHAAR_CARD', 
          file: { name: 'aadhaar_demo.jpg' },
          uploadedAt: new Date()
        },
        {
          type: 'SELFIE',
          file: { name: 'selfie_demo.jpg' },
          uploadedAt: new Date()
        }
      ];
    }

    // Update kycData with current documents
    setKycData(prev => ({
      ...prev,
      documents: documentsToVerify
    }));

    setVerificationState(prev => ({
      ...prev,
      isVerifying: true,
      currentStep: 'authenticity_check',
      progress: 0,
      error: null
    }));

    setSuccess('');

    try {
      console.log('Starting verification steps...');
      
      // Step 1: Document Authenticity Check
      await runAuthenticityCheck(documentsToVerify);
      
      // Step 2: Data Extraction
      await runDataExtraction(documentsToVerify);
      
      // Step 3: Face Verification
      await runFaceVerification(documentsToVerify);
      
      // Step 4: Consistency Check
      await runConsistencyCheck();
      
      // Step 5: Final Review
      await runFinalReview();

    } catch (error) {
      console.error('Verification failed:', error);
      setVerificationState(prev => ({
        ...prev,
        isVerifying: false,
        error: error.message || 'Verification failed'
      }));
    }
  };

  const runAuthenticityCheck = async (documents = kycData.documents) => {
    updateStepStatus('authenticity_check', 'active');
    setVerificationState(prev => ({ ...prev, progress: 15 }));

    const authenticityResults = {};
    
    for (const doc of documents) {
      try {
        // Simulate authenticity check with random results
        const confidence = Math.random() * 0.3 + 0.7; // 0.7 to 1.0
        const isAuthentic = confidence > 0.75;
        
        authenticityResults[doc.type] = {
          isAuthentic,
          confidence,
          score: Math.round(confidence * 100)
        };
        
        // Simulate processing time
        await new Promise(resolve => setTimeout(resolve, 1500));
      } catch (error) {
        authenticityResults[doc.type] = { error: error.message, isAuthentic: false };
      }
    }

    console.log('Authenticity check results:', authenticityResults);
    
    setKycData(prev => ({
      ...prev,
      verificationResults: {
        ...prev.verificationResults,
        authenticity: authenticityResults
      }
    }));

    updateStepStatus('authenticity_check', 'completed');
    setVerificationState(prev => ({ ...prev, progress: 25 }));
  };

  const runDataExtraction = async (documents = kycData.documents) => {
    updateStepStatus('data_extraction', 'active');
    setVerificationState(prev => ({ ...prev, progress: 35 }));

    const extractedData = {};
    
    for (const doc of documents) {
      try {
        // Simulate OCR data extraction
        const mockExtractedData = await simulateDataExtraction(doc.type);
        extractedData[doc.type] = mockExtractedData;
        
        await new Promise(resolve => setTimeout(resolve, 2000));
      } catch (error) {
        extractedData[doc.type] = { error: error.message };
      }
    }

    console.log('Data extraction results:', extractedData);
    
    setKycData(prev => ({
      ...prev,
      verificationResults: {
        ...prev.verificationResults,
        extractedData: extractedData
      }
    }));

    updateStepStatus('data_extraction', 'completed');
    setVerificationState(prev => ({ ...prev, progress: 50 }));
  };

  const runFaceVerification = async (documents = kycData.documents) => {
    updateStepStatus('face_verification', 'active');
    setVerificationState(prev => ({ ...prev, progress: 60 }));

    const identityDoc = documents.find(doc => doc.type === 'PAN_CARD' || doc.type === 'AADHAAR_CARD');
    const selfieDoc = documents.find(doc => doc.type === 'SELFIE');
    
    let faceVerificationResult = { skipped: true, reason: 'No suitable documents for face verification' };

    if (identityDoc && selfieDoc) {
      try {
        // Simulate face verification
        const confidence = Math.random() * 0.4 + 0.6; // 0.6 to 1.0
        const isMatch = confidence > 0.75;
        
        faceVerificationResult = {
          isMatch,
          confidence,
          score: Math.round(confidence * 100),
          skipped: false
        };
        
        await new Promise(resolve => setTimeout(resolve, 3000));
      } catch (error) {
        faceVerificationResult = { error: error.message, isMatch: false };
      }
    }

    console.log('Face verification results:', faceVerificationResult);
    
    setKycData(prev => ({
      ...prev,
      verificationResults: {
        ...prev.verificationResults,
        faceVerification: faceVerificationResult
      }
    }));

    updateStepStatus('face_verification', 'completed');
    setVerificationState(prev => ({ ...prev, progress: 75 }));
  };

  const runConsistencyCheck = async () => {
    updateStepStatus('consistency_check', 'active');
    setVerificationState(prev => ({ ...prev, progress: 85 }));

    const extractedData = kycData.verificationResults.extractedData || {};
    const formData = { ...kycData.personalInfo, ...kycData.businessInfo };

    try {
      // Simulate consistency check
      const confidenceScore = Math.random() * 0.3 + 0.7; // 0.7 to 1.0
      const isConsistent = confidenceScore > 0.8;
      
      const consistencyResult = {
        isConsistent,
        confidenceScore,
        score: Math.round(confidenceScore * 100)
      };
      
      console.log('Consistency check results:', consistencyResult);
      
      setKycData(prev => ({
        ...prev,
        verificationResults: {
          ...prev.verificationResults,
          consistency: consistencyResult
        }
      }));

      await new Promise(resolve => setTimeout(resolve, 1500));
    } catch (error) {
      setKycData(prev => ({
        ...prev,
        verificationResults: {
          ...prev.verificationResults,
          consistency: { error: error.message, isConsistent: false }
        }
      }));
    }

    updateStepStatus('consistency_check', 'completed');
    setVerificationState(prev => ({ ...prev, progress: 95 }));
  };

  const runFinalReview = async () => {
    updateStepStatus('final_review', 'active');
    setVerificationState(prev => ({ ...prev, progress: 98 }));

    const finalResults = calculateFinalResults();
    
    try {
      // Update KYC status via API
      await api.post(`/api/kyc/status/${kycData.merchantId}`, finalResults);
      
      // Save KYC status to localStorage for persistence using utility
      const kycStatusData = {
        status: finalResults.status,
        fullName: kycData.personalInfo?.fullName || 'Demo User',
        businessName: kycData.personalInfo?.businessName || 'Demo Business',
        panVerified: true,
        gstinVerified: kycData.personalInfo?.gstin ? true : false,
        registeredAddress: kycData.personalInfo?.address || 'Demo Address',
        city: kycData.personalInfo?.city || 'Demo City',
        state: kycData.personalInfo?.state || 'Demo State',
        bankVerified: true,
        documentsUploaded: true,
        submittedAt: new Date().toISOString(),
        completionPercentage: finalResults.finalScore || 85,
        verificationResults: finalResults
      };

      // Save using the new utility (merchant-specific)
      KYCStatusManager.saveKYCStatus(kycData.merchantId, kycStatusData);
      
      // Also save to legacy format for backward compatibility
      localStorage.setItem('kycStatus', JSON.stringify(kycStatusData));
      localStorage.setItem('kycVerificationComplete', 'true');

      // Debug logging
      console.log('🔍 KYC Verification completed for merchant:', kycData.merchantId);
      console.log('📊 Final results:', finalResults);
      console.log('💾 Saved status data:', kycStatusData);
      KYCDebugHelper.checkMerchantStatus(kycData.merchantId);

      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('kycStatusUpdated', { 
        detail: { merchantId: kycData.merchantId, status: kycStatusData } 
      }));
      
      setVerificationState({
        isVerifying: false,
        currentStep: 'completed',
        progress: 100,
        results: finalResults,
        error: null
      });

      updateStepStatus('final_review', 'completed');

      // Show completion notification
      if (window.showNotification) {
        const message = finalResults.status === 'APPROVED' 
          ? `🎉 KYC Verification Completed! Score: ${finalResults.finalScore || 85}%`
          : `📋 KYC Verification Submitted for Review. Score: ${finalResults.finalScore || 85}%`;
        
        window.showNotification(message, finalResults.status === 'APPROVED' ? 'success' : 'info');
      }
      
    } catch (error) {
      setVerificationState(prev => ({
        ...prev,
        isVerifying: false,
        error: 'Failed to update KYC status'
      }));
    }
  };

  const calculateFinalResults = () => {
    console.log('Calculating final results with verification data:', kycData.verificationResults);
    
    const { authenticity, extractedData, faceVerification, consistency } = kycData.verificationResults || {};
    
    let totalScore = 0;
    let maxScore = 100; // Set a base max score
    const stepScores = {};

    // Authenticity Score (40% weight)
    if (authenticity && Object.keys(authenticity).length > 0) {
      const authScores = Object.values(authenticity).map(result => 
        result.isAuthentic ? (result.confidence || result.score || 85) : 0
      );
      const avgAuthScore = authScores.length > 0 ? authScores.reduce((a, b) => a + b, 0) / authScores.length : 0;
      stepScores.authenticity = avgAuthScore;
      totalScore += avgAuthScore * 0.4;
    } else {
      // Default authenticity score for demo
      stepScores.authenticity = 88;
      totalScore += 88 * 0.4;
    }

    // Data Extraction Score (20% weight)
    if (extractedData && Object.keys(extractedData).length > 0) {
      const extractionSuccess = Object.values(extractedData).filter(data => !data.error).length;
      const totalExtractions = Object.keys(extractedData).length;
      const extractionScore = totalExtractions > 0 ? (extractionSuccess / totalExtractions) * 100 : 0;
      stepScores.dataExtraction = extractionScore;
      totalScore += extractionScore * 0.2;
    } else {
      // Default data extraction score for demo
      stepScores.dataExtraction = 92;
      totalScore += 92 * 0.2;
    }

    // Face Verification Score (25% weight)
    if (faceVerification && !faceVerification.skipped) {
      const faceScore = faceVerification.isMatch ? (faceVerification.confidence || faceVerification.score || 85) * 100 : 0;
      stepScores.faceVerification = faceScore;
      totalScore += faceScore * 0.25;
    } else {
      // Default face verification score for demo
      stepScores.faceVerification = 85;
      totalScore += 85 * 0.25;
    }

    // Consistency Score (15% weight)
    if (consistency) {
      const consistencyScore = consistency.isConsistent ? (consistency.confidenceScore || consistency.score || 90) * 100 : 0;
      stepScores.consistency = consistencyScore;
      totalScore += consistencyScore * 0.15;
    } else {
      // Default consistency score for demo
      stepScores.consistency = 90;
      totalScore += 90 * 0.15;
    }

    const finalScore = Math.round(totalScore);
    const status = determineKYCStatus(finalScore, stepScores);

    console.log('Final calculation results:', { finalScore, status, stepScores });

    return {
      finalScore,
      status,
      stepScores,
      recommendations: generateRecommendations(stepScores, kycData.verificationResults || {}),
      timestamp: new Date().toISOString(),
      merchantId: kycData.merchantId
    };
  };

  const determineKYCStatus = (finalScore, stepScores) => {
    // Critical failures
    if (stepScores.authenticity < 40) return 'REJECTED';  // Lowered from 50 to 40
    if (stepScores.faceVerification !== undefined && stepScores.faceVerification < 50) return 'REJECTED';  // Lowered from 60 to 50
    
    // Score-based determination - More lenient for demo
    if (finalScore >= 75) return 'APPROVED';  // Lowered from 85 to 75
    if (finalScore >= 60) return 'PENDING_REVIEW';  // Lowered from 70 to 60, changed to PENDING_REVIEW
    return 'REJECTED';
  };

  const generateRecommendations = (stepScores, verificationResults) => {
    const recommendations = [];

    if (stepScores.authenticity < 80) {
      recommendations.push('Document quality could be improved. Consider uploading clearer, high-resolution images.');
    }

    if (stepScores.faceVerification !== undefined && stepScores.faceVerification < 80) {
      recommendations.push('Face verification confidence is low. Ensure good lighting and clear facial visibility in photos.');
    }

    if (stepScores.consistency < 80) {
      recommendations.push('Data inconsistencies detected. Please verify that all information matches across documents.');
    }

    if (stepScores.dataExtraction < 90) {
      recommendations.push('Some data could not be extracted clearly. Consider uploading documents with better text clarity.');
    }

    return recommendations;
  };

  const simulateDataExtraction = async (documentType) => {
    const mockData = {
      'PAN_CARD': {
        name: 'John Doe',
        panNumber: 'ABCDE1234F',
        dateOfBirth: '15/01/1990',
        fatherName: 'Robert Doe'
      },
      'AADHAAR_CARD': {
        name: 'John Doe',
        aadhaarNumber: '1234-5678-9012',
        dateOfBirth: '15/01/1990',
        address: '123 Test Street, Test City, 123456',
        gender: 'Male'
      },
      'CANCELLED_CHEQUE': {
        accountHolderName: 'John Doe',
        accountNumber: '1234567890123456',
        ifscCode: 'SBIN0001234',
        bankName: 'State Bank of India'
      },
      'GST_CERTIFICATE': {
        legalName: 'John Doe Business Pvt Ltd',
        gstin: '22AAAAA0000A1Z5',
        panNumber: 'ABCDE1234F'
      }
    };

    return mockData[documentType] || {};
  };

  const updateStepStatus = (stepId, status) => {
    setVerificationSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, status } : step
    ));
  };

  const getStepStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'step-completed';
      case 'active': return 'step-active';
      case 'failed': return 'step-failed';
      default: return 'step-pending';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return '#28a745';
      case 'MANUAL_REVIEW': return '#ffc107';
      case 'REJECTED': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const exportResults = () => {
    if (!verificationState.results) return;

    const exportData = {
      merchantId: kycData.merchantId,
      verificationDate: new Date().toISOString(),
      finalScore: verificationState.results.finalScore,
      status: verificationState.results.status,
      stepScores: verificationState.results.stepScores,
      recommendations: verificationState.results.recommendations,
      documents: kycData.documents.map(doc => ({
        type: doc.type,
        fileName: doc.file?.name || 'Unknown',
        uploadedAt: doc.uploadedAt
      }))
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `kyc-verification-results-${kycData.merchantId}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    if (!verificationState.results) return;
    
    // Create a simple HTML report
    const reportHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>KYC Verification Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; }
          .header { text-align: center; margin-bottom: 30px; }
          .status { padding: 10px; border-radius: 5px; margin: 20px 0; }
          .approved { background: #d4edda; color: #155724; }
          .review { background: #fff3cd; color: #856404; }
          .rejected { background: #f8d7da; color: #721c24; }
          .score { font-size: 24px; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>KYC Verification Report</h1>
          <p>Merchant ID: ${kycData.merchantId}</p>
          <p>Date: ${new Date().toLocaleDateString()}</p>
        </div>
        
        <div class="status ${verificationState.results.status.toLowerCase()}">
          <h2>Status: ${verificationState.results.status}</h2>
          <div class="score">Final Score: ${verificationState.results.finalScore}%</div>
        </div>
        
        <h3>Step-by-Step Scores</h3>
        <table>
          <tr><th>Verification Step</th><th>Score</th></tr>
          ${Object.entries(verificationState.results.stepScores || {}).map(([step, score]) => 
            `<tr><td>${step.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</td><td>${Math.round(score)}%</td></tr>`
          ).join('')}
        </table>
        
        <h3>Recommendations</h3>
        <ul>
          ${(verificationState.results.recommendations || []).map(rec => `<li>${rec}</li>`).join('')}
        </ul>
        
        <h3>Uploaded Documents</h3>
        <table>
          <tr><th>Document Type</th><th>File Name</th><th>Upload Date</th></tr>
          ${kycData.documents.map(doc => 
            `<tr><td>${doc.type}</td><td>${doc.file?.name || 'Unknown'}</td><td>${new Date(doc.uploadedAt).toLocaleDateString()}</td></tr>`
          ).join('')}
        </table>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(reportHTML);
    printWindow.document.close();
    printWindow.print();
  };

  // Show loading if no user data
  if (!currentUser) {
    return (
      <div className="kyc-verification-center">
        <div className="verification-header">
          <h2>🔐 KYC Verification Center</h2>
          <p>Loading user data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="kyc-verification-center">
      <div className="verification-header">
        <h2>🔐 KYC Verification Center</h2>
        <p>Complete document upload and automated verification process</p>
        
        <div className="demo-mode-banner">
          <span className="demo-icon">🚀</span>
          <span className="demo-text">
            <strong>Demo Mode:</strong> Running with simulated data - No backend required
          </span>
        </div>
        
        <div className="tab-navigation">
          <button 
            className={`tab-btn ${activeTab === 'verification' ? 'active' : ''}`}
            onClick={() => setActiveTab('verification')}
          >
            📄 Verification
          </button>
          <button 
            className={`tab-btn ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            📊 Analytics
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'verification' && (
        <>
          {/* Verification Steps Progress */}
          <div className="verification-steps">
        <h3>Verification Progress</h3>
        <div className="steps-container">
          {verificationSteps.map((step, index) => (
            <div key={step.id} className={`verification-step ${getStepStatusClass(step.status)}`}>
              <div className="step-indicator">
                <span className="step-icon">{step.icon}</span>
                <span className="step-number">{index + 1}</span>
              </div>
              <div className="step-content">
                <h4>{step.label}</h4>
                <span className="step-status">{step.status}</span>
              </div>
              {index < verificationSteps.length - 1 && (
                <div className="step-connector"></div>
              )}
            </div>
          ))}
        </div>
        
        {verificationState.isVerifying && (
          <div className="overall-progress">
            <div className="progress-info">
              <span>Overall Progress: {verificationState.progress}%</span>
              <span className="progress-status">
                {verificationState.currentStep === 'authenticity_check' && '🔍 Checking document authenticity...'}
                {verificationState.currentStep === 'data_extraction' && '📊 Extracting document data...'}
                {verificationState.currentStep === 'face_verification' && '👤 Verifying face match...'}
                {verificationState.currentStep === 'consistency_check' && '✅ Checking data consistency...'}
                {verificationState.currentStep === 'final_review' && '🎯 Finalizing results...'}
              </span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill animated" 
                style={{ width: `${verificationState.progress}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {verificationState.error && (
        <div className="alert alert-error">
          <strong>Verification Error:</strong> {verificationState.error}
        </div>
      )}

      {/* Success Display */}
      {success && (
        <div className="alert alert-success">
          <strong>Success:</strong> {success}
        </div>
      )}

      {/* Document Upload Section */}
      {verificationState.currentStep === 'document_upload' && (
        <SimpleDocumentUpload
          merchantId={kycData.merchantId}
          onDocumentUploaded={handleDocumentUploaded}
          onVerificationComplete={handleVerificationComplete}
        />
      )}

      {/* Verification Controls */}
      {!verificationState.isVerifying && verificationState.currentStep === 'document_upload' && (
        <div className="verification-controls">
          <button
            className="btn btn-primary btn-lg"
            onClick={startComprehensiveVerification}
            disabled={kycData.documents.length === 0}
          >
            🚀 Start Comprehensive Verification
          </button>
          
          <div className="verification-info">
            <h4>Comprehensive Verification includes:</h4>
            <ul>
              <li>✅ Document authenticity validation using AI</li>
              <li>✅ OCR-based data extraction and validation</li>
              <li>✅ Biometric face matching verification</li>
              <li>✅ Cross-document data consistency checks</li>
              <li>✅ Automated KYC status determination</li>
              <li>✅ Detailed recommendations and feedback</li>
            </ul>
          </div>
        </div>
      )}

      {/* Verification Results */}
      {verificationState.results && (
        <div className="verification-results">
          <div className="results-header">
            <div className="results-title">
              <h3>🎯 Verification Results</h3>
              <div className="export-buttons">
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={exportResults}
                  title="Export as JSON"
                >
                  📄 Export JSON
                </button>
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={exportPDF}
                  title="Print/Save as PDF"
                >
                  🖨️ Print Report
                </button>
              </div>
            </div>
            <div className="final-score">
              <span>Final Score:</span>
              <span className="score-value">{verificationState.results.finalScore}%</span>
            </div>
          </div>

          <div className="status-result">
            <h4 style={{ color: getStatusColor(verificationState.results.status) }}>
              Status: {verificationState.results.status}
            </h4>
            <p>
              {verificationState.results.status === 'APPROVED' && 'Congratulations! Your KYC has been approved automatically.'}
              {verificationState.results.status === 'MANUAL_REVIEW' && 'Your KYC requires manual review by our team.'}
              {verificationState.results.status === 'REJECTED' && 'Your KYC has been rejected. Please review the recommendations below.'}
            </p>
          </div>

          {/* Step Scores */}
          {verificationState.results.stepScores && (
            <div className="step-scores">
              <h4>Step-by-Step Scores</h4>
              <div className="scores-grid">
                {Object.entries(verificationState.results.stepScores).map(([step, score]) => (
                  <div key={step} className="score-card">
                    <h5>{step.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</h5>
                    <div className="score-circle">
                      <span className="score-number">{Math.round(score)}%</span>
                    </div>
                    <div className="score-bar">
                      <div 
                        className="score-fill" 
                        style={{ 
                          width: `${score}%`,
                          backgroundColor: score >= 75 ? '#28a745' : score >= 60 ? '#ffc107' : '#dc3545'  // Lowered thresholds
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {verificationState.results.recommendations && verificationState.results.recommendations.length > 0 && (
            <div className="recommendations">
              <h4>💡 Recommendations</h4>
              <ul>
                {verificationState.results.recommendations.map((rec, index) => (
                  <li key={index}>{rec}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Next Steps */}
          <div className="next-steps">
            <h4>🎯 Next Steps</h4>
            {verificationState.results.status === 'APPROVED' && (
              <div className="next-step-card approved">
                <p>✅ Your KYC is complete! You can now start accepting payments.</p>
                <button 
                  className="btn btn-success"
                  onClick={() => window.location.href = '/dashboard'}
                >
                  Go to Dashboard
                </button>
              </div>
            )}
            
            {verificationState.results.status === 'MANUAL_REVIEW' && (
              <div className="next-step-card review">
                <p>⏳ Our team will review your KYC within 24-48 hours. You'll receive an email notification.</p>
                <button 
                  className="btn btn-secondary"
                  onClick={() => window.location.href = '/kyc/help'}
                >
                  Contact Support
                </button>
              </div>
            )}
            
            {verificationState.results.status === 'REJECTED' && (
              <div className="next-step-card rejected">
                <p>❌ Please address the issues mentioned in recommendations and resubmit your documents.</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => window.location.reload()}
                >
                  Start Over
                </button>
              </div>
            )}
          </div>
        </div>
      )}
        </>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <KYCAnalytics merchantId={kycData.merchantId} />
      )}
    </div>
  );
};

export default KYCVerificationCenter;